import { useEffect, useMemo, useRef, useState } from 'react';

import { CommentThread } from '@/features/canvas/types';
import { Member } from '@/features/backlog/types';
import { stripMentions } from '@/features/backlog/mentions';
import { commentTime } from '@/features/backlog/format';
import MemberAvatar from '@/components/member-avatar';
import { cn } from '@/components/ui/cn';
import { GLASS } from './glass';
import { MS, MS_ICON } from './ms';

const iconBtn = 'relative flex h-8 w-8 items-center justify-center rounded-lg text-fg-muted transition-colors hover:bg-layer hover:text-fg';

function snippet(body: string): string {
  const first = stripMentions(body).split('\n').find((l) => l.trim()) ?? '';
  return first.length > 80 ? `${first.slice(0, 80)}…` : first;
}

/**
 * The top-right comments flyout: a button (with an open-thread count badge) that drops a list of all
 * threads, split Open / Resolved. Clicking a row flies the camera to that pin and opens its thread.
 * Lives in the TopBar's right cluster, immediately left of the presence avatars.
 */
export default function CommentFlyout({
  threads,
  members,
  unread,
  onFocus,
}: {
  threads: CommentThread[];
  members: Member[];
  /** Thread root ids that are new-to-me (drives the button badge + row dots). */
  unread: Set<string>;
  onFocus: (threadId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const membersById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);

  const openThreads = useMemo(() => threads.filter((t) => !t.root.resolved), [threads]);
  const resolvedThreads = useMemo(() => threads.filter((t) => t.root.resolved), [threads]);

  // Close on an outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const Row = ({ t }: { t: CommentThread }) => {
    const mem = t.root.author_id ? membersById.get(t.root.author_id) : undefined;
    return (
      <button
        type="button"
        onClick={() => {
          onFocus(t.root.id);
          setOpen(false);
        }}
        className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-layer"
      >
        <MemberAvatar member={mem ?? ({ id: '', name: t.root.author_name || 'Someone', color: '#71717a', active: true } as Member)} size={26} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {unread.has(t.root.id) ? <span className="h-2 w-2 shrink-0 rounded-full bg-brand" title="New activity" /> : null}
            <span className={cn('truncate text-[12px] text-fg', unread.has(t.root.id) ? 'font-semibold' : 'font-medium')}>{mem?.name || t.root.author_name || 'Someone'}</span>
            <span className="shrink-0 text-[10px] text-fg-faint">{commentTime(t.root.created_at)}</span>
            {t.count > 1 ? <span className="ml-auto shrink-0 text-[10px] text-fg-faint">{t.count - 1} repl{t.count - 1 === 1 ? 'y' : 'ies'}</span> : null}
          </div>
          <div className="truncate text-[12px] text-fg-soft">{snippet(t.root.body) || <span className="italic text-fg-faint">(empty)</span>}</div>
        </div>
        {t.root.resolved ? <MS icon={MS_ICON.circle_check} size={14} className="mt-0.5 shrink-0 text-emerald-500" /> : null}
      </button>
    );
  };

  return (
    <div ref={wrapRef} className="relative">
      <button type="button" className={iconBtn} title="Comments" onClick={() => setOpen((v) => !v)}>
        <MS icon={MS_ICON.comment_dots} size={18} />
        {unread.size > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[9px] font-bold leading-none text-white" title={`${unread.size} new`}>
            {unread.size}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className={cn(GLASS, 'absolute right-0 top-full z-40 mt-2 max-h-[70vh] w-[320px] origin-top-right scale-[0.8] overflow-y-auto rounded-2xl p-1.5')}>
          {threads.length === 0 ? (
            <div className="px-3 py-6 text-center text-[12px] text-fg-faint">
              No comments yet. Pick the comment tool and click the canvas to start a thread.
            </div>
          ) : (
            <>
              {openThreads.length > 0 ? (
                <>
                  <div className="px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">Open · {openThreads.length}</div>
                  {openThreads.map((t) => (
                    <Row key={t.root.id} t={t} />
                  ))}
                </>
              ) : null}
              {resolvedThreads.length > 0 ? (
                <>
                  <div className="px-2.5 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">Resolved · {resolvedThreads.length}</div>
                  {resolvedThreads.map((t) => (
                    <Row key={t.root.id} t={t} />
                  ))}
                </>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
